// Firebase Authentication Helpers
// Google sign-in, email/password, and user management

import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  reauthenticateWithPopup,
  updatePassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  UserCredential,
} from "firebase/auth"
import { auth } from "./config"
import { createUserInFirestore, getUserFromFirestore, getUserByEmailFromFirestore } from "./firestore"
import { normalizeEmail } from "@/lib/utils"

export { normalizeEmail }

const googleProvider = new GoogleAuthProvider()

export async function signInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(auth, googleProvider)
}

export async function signInWithEmailPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  const normalized = normalizeEmail(email)
  if (!normalized || !password) {
    throw new Error("Email and password are required")
  }
  return signInWithEmailAndPassword(auth, normalized, password)
}

export async function signUpWithEmailPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  const normalized = normalizeEmail(email)
  if (!normalized || !password) {
    throw new Error("Email and password are required")
  }
  return createUserWithEmailAndPassword(auth, normalized, password)
}

/** Whether the current Firebase user has email/password provider (can sign in with password). */
export function hasPasswordProvider(firebaseUser: FirebaseUser): boolean {
  return firebaseUser.providerData?.some((p) => p.providerId === "password") ?? false
}

/**
 * Create a password for a Google-only user. Requires re-auth (e.g. Google popup) then sets password.
 * Call only when user is already signed in (e.g. from Settings).
 */
export async function createPasswordForCurrentUser(newPassword: string): Promise<void> {
  const u = auth.currentUser
  if (!u) throw new Error("Not signed in")
  if (!newPassword || newPassword.length < 8) {
    throw new Error("Password must be at least 8 characters")
  }
  await reauthenticateWithPopup(u, googleProvider)
  await updatePassword(u, newPassword)
}

export async function signOut(): Promise<void> {
  return firebaseSignOut(auth)
}

export function onAuthChange(callback: (user: FirebaseUser | null) => void) {
  return onAuthStateChanged(auth, callback)
}

export async function ensureUserExists(firebaseUser: FirebaseUser) {
  try {
    const email = firebaseUser.email || ""
    const normalized = normalizeEmail(email)
    const googlePhotoURL = firebaseUser.photoURL || undefined

    // Account unification: if a user with this email already exists (e.g. from email/password), use that account
    const existingByEmail = normalized ? await getUserByEmailFromFirestore(normalized) : null
    if (existingByEmail) {
      if (existingByEmail.id === firebaseUser.uid) {
        // Same user (e.g. signed in with Google before); update profile if needed
        const { updateUserInFirestore } = await import("./firestore")
        if (googlePhotoURL && existingByEmail.avatar !== googlePhotoURL) {
          await updateUserInFirestore(firebaseUser.uid, { avatar: googlePhotoURL })
        }
        const googleDisplayName = firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User"
        if (googleDisplayName && existingByEmail.username !== googleDisplayName) {
          await updateUserInFirestore(firebaseUser.uid, { username: googleDisplayName })
        }
        return getUserFromFirestore(firebaseUser.uid)
      }
      // Different uid but same email: return existing user so app uses same account (one user per email)
      console.log("[Firebase Auth] ensureUserExists: linking to existing account by email, uid=", firebaseUser.uid, "existingId=", existingByEmail.id)
      return existingByEmail
    }

    const userDoc = await getUserFromFirestore(firebaseUser.uid)
    if (userDoc) {
      const { updateUserInFirestore } = await import("./firestore")
      if (googlePhotoURL && userDoc.avatar !== googlePhotoURL) {
        await updateUserInFirestore(firebaseUser.uid, { avatar: googlePhotoURL })
      }
      const googleDisplayName = firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User"
      if (googleDisplayName && userDoc.username !== googleDisplayName) {
        await updateUserInFirestore(firebaseUser.uid, { username: googleDisplayName })
      }
      return getUserFromFirestore(firebaseUser.uid)
    }

    console.log("[Firebase Auth] ensureUserExists: creating user in Firestore for uid=", firebaseUser.uid)
    await createUserInFirestore({
      id: firebaseUser.uid,
      email,
      normalizedEmail: normalized || undefined,
      username: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User",
      avatar: googlePhotoURL,
      preferences: {
        theme: "system",
        riskTolerance: "medium",
        investmentFocus: "diversified",
        notifications: {
          email: true,
          targetPrice: true,
          ratingChange: true,
          majorNews: true,
        },
      },
      portfolio: {
        holdings: [],
        cash: 0,
        lastUpdated: new Date().toISOString(),
      },
      createdAt: new Date().toISOString(),
      isAdmin: false,
    })
    return getUserFromFirestore(firebaseUser.uid)
  } catch (e) {
    console.error("[Firebase Auth] ensureUserExists error:", e)
    console.error("[Firebase Auth] uid:", firebaseUser.uid, "code:", (e as any)?.code, "message:", (e as any)?.message)
    throw e
  }
}

